import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation, useOutletContext } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useAuthStore, isSeniorStaffOrAbove, isStaff } from '@/stores/authStore'
import { authService } from '@/services/authService'
import { LoginForm } from '@/types'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { TwoFactorLoginForm } from '@/components/auth/TwoFactorLoginForm'
import { useTranslation } from '@/i18n/useTranslation'
import type { AuthOutletContext } from '@/layouts/AuthLayout'

const CustomerLogin = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isViewingAsCustomer, setIsViewingAsCustomer] = useState(false)
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const { applySession } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('common')
  const { shopBranding } = useOutletContext<AuthOutletContext>()
  const { config, primary, shopQuery, slug, loading } = shopBranding
  const shopThemed = Boolean(slug && config)

  const searchParams = new URLSearchParams(location.search)
  const customerId = searchParams.get('customer_id')
  const redirectParam = searchParams.get('redirect')
  const orderParam = searchParams.get('order')
  const fromState = location.state?.from as
    | { pathname?: string; search?: string }
    | string
    | undefined
  const fromPath =
    typeof fromState === 'string'
      ? fromState
      : fromState?.pathname
        ? `${fromState.pathname}${fromState.search || ''}`
        : slug
          ? `/shop/${slug}`
          : '/customer'

  const handleViewAsCustomer = async (customerId: number) => {
    setIsLoading(true)
    try {
      const response = await authService.staffViewAsCustomer(customerId)

      if (response.success && response.data) {
        const userData = {
          ...response.data.user,
          is_web_user: true,
        }

        applySession({
          token: response.data.token,
          user: userData,
          expires_in: response.data.expires_in,
          idle_timeout_minutes: response.data.idle_timeout_minutes,
          max_session_expires_at: response.data.max_session_expires_at,
        })

        await new Promise((resolve) => setTimeout(resolve, 100))

        toast.success(
          t('auth.viewAsCustomer', {
            name: response.data.user.name || response.data.user.email,
          }),
        )
        navigate('/customer', { replace: true })
      } else {
        toast.error(response.message || t('auth.viewAsCustomerFailed'))
        setIsViewingAsCustomer(false)
      }
    } catch (error: any) {
      console.error('View as customer error:', error)
      toast.error(error?.response?.data?.message || t('auth.viewAsCustomerFailed'))
      setIsViewingAsCustomer(false)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (customerId && isStaff() && isSeniorStaffOrAbove()) {
      setIsViewingAsCustomer(true)
      handleViewAsCustomer(parseInt(customerId, 10))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>()

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      const response = await authService.customerLogin({
        ...data,
        ...(config?.shop_id ? { shop_id: config.shop_id } : {}),
      })

      if (response.success && response.data?.pending_2fa && response.data.challenge_id) {
        setChallengeId(response.data.challenge_id)
        return
      }

      if (response.success && response.data?.token && response.data.user) {
        const userData = {
          ...response.data.user,
          is_web_user: true,
        }

        applySession({
          token: response.data.token,
          user: userData,
          expires_in: response.data.expires_in,
          idle_timeout_minutes: response.data.idle_timeout_minutes,
          max_session_expires_at: response.data.max_session_expires_at,
        })
        toast.success(t('auth.welcomeBack'))

        setTimeout(() => {
          if (redirectParam) {
            const target =
              orderParam && redirectParam.includes('orders')
                ? `${redirectParam}${redirectParam.includes('?') ? '&' : '?'}order=${encodeURIComponent(orderParam)}`
                : redirectParam
            navigate(target, { replace: true })
          } else {
            navigate(fromPath, { replace: true })
          }
        }, 150)
      } else {
        toast.error(response.message || t('auth.loginFailed'))
      }
    } catch (error: any) {
      const code = error?.response?.data?.code
      if (code === 'email_verification_required') {
        toast.error(error?.response?.data?.message || t('auth.customerLogin.verifyEmailRequired'))
        const emailForResend = data.email
        if (config?.shop_id && emailForResend) {
          try {
            await authService.resendCustomerVerification({
              email: emailForResend,
              shop_id: config.shop_id,
              redirect_path: redirectParam || fromPath || undefined,
            })
            toast.success(t('auth.customerLogin.verifyEmailResent'))
          } catch {
            /* ignore resend failure; main message already shown */
          }
        }
      } else {
        toast.error(error?.response?.data?.message || t('auth.loginFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const linkClass = shopThemed
    ? 'font-medium hover:opacity-85'
    : 'font-medium text-primary-600 hover:text-primary-500'
  const linkStyle = shopThemed ? { color: primary } : undefined
  const submitClass = shopThemed
    ? 'inline-flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60'
    : 'btn btn-primary btn-lg w-full'
  const submitStyle = shopThemed
    ? { backgroundColor: primary, color: '#ffffff' }
    : undefined

  if (challengeId) {
    return (
      <div>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Two-factor authentication</h2>
        </div>
        <TwoFactorLoginForm
          challengeId={challengeId}
          onCancel={() => setChallengeId(null)}
          onSuccess={(data) => {
            applySession({ ...data, user: { ...data.user, is_web_user: true } })
            toast.success(t('auth.welcomeBack'))
            navigate(fromPath, { replace: true })
          }}
        />
      </div>
    )
  }

  if (isViewingAsCustomer || (customerId && isStaff() && isSeniorStaffOrAbove())) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-600">{t('auth.loadingCustomerPortal')}</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Platform ShopQuay has no customer storefront — require a shop slug (?shop=…).
  if (!slug || !config?.shop_id) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.customerAuthBlocked.title')}</h2>
        <p className="mt-3 text-sm text-gray-600">{t('auth.customerAuthBlocked.loginBody')}</p>
        <div className="mt-6">
          <Link
            to="/auth/staff/login"
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            {t('auth.customerLogin.staffLoginLink')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.customerLogin.title')}</h2>
        <p className="mt-2 text-sm text-gray-600">{t('auth.customerLogin.subtitle')}</p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor="email" className="form-label">
            {t('auth.emailAddress')}
          </label>
          <input
            {...register('email', {
              required: t('auth.validation.emailRequired'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('auth.validation.invalidEmail'),
              },
            })}
            id="email"
            type="email"
            className={`input ${errors.email ? 'input-error' : ''}`}
            placeholder={t('auth.placeholderEmail')}
          />
          {errors.email && <p className="form-error">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="form-label">
            {t('auth.password')}
          </label>
          <input
            {...register('password', {
              required: t('auth.validation.passwordRequired'),
              minLength: {
                value: 8,
                message: t('auth.validation.passwordMinLength'),
              },
            })}
            id="password"
            type="password"
            autoComplete="current-password"
            className={`input ${errors.password ? 'input-error' : ''}`}
            placeholder={t('auth.placeholderPassword')}
          />
          {errors.password && <p className="form-error">{errors.password.message}</p>}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm">
            <Link
              to={`/auth/forgot-password${shopQuery}`}
              className={linkClass}
              style={linkStyle}
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
        </div>

        <div>
          <button type="submit" disabled={isLoading} className={submitClass} style={submitStyle}>
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                {t('auth.signingIn')}
              </>
            ) : (
              t('auth.signIn')
            )}
          </button>
        </div>
      </form>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">{t('auth.customerLogin.newCustomer')}</span>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link
            to={`/auth/customer/register${(() => {
              const params = new URLSearchParams(shopQuery.startsWith('?') ? shopQuery.slice(1) : shopQuery)
              if (redirectParam) params.set('redirect', redirectParam)
              const q = params.toString()
              return q ? `?${q}` : ''
            })()}`}
            state={location.state}
            className={linkClass}
            style={linkStyle}
          >
            {t('auth.customerLogin.createAccount')}
          </Link>
        </div>

        {slug ? (
          <div className="mt-4 text-center">
            <Link
              to={`/shop/${encodeURIComponent(slug)}`}
              className="text-sm font-medium text-gray-600 hover:text-gray-500"
            >
              {t('auth.customerLogin.backToShop')}
            </Link>
          </div>
        ) : null}

        <div className="mt-4 text-center">
          <Link
            to="/auth/staff/login"
            className="text-sm font-medium text-gray-600 hover:text-gray-500"
          >
            {t('auth.customerLogin.staffLoginLink')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default CustomerLogin
